import { useCallback, useMemo, useState } from "react"
import { useQuery } from "urql"

import "../graphql/vendorApLedger.query"

import {
  GetVendorApLedgerEntriesDocument,
  SortingOrder,
  type VendorApLedgerEntry,
  type VendorApLedgerListFilter,
  VendorApLedgerListSortField,
} from "@/graphql/codegen/graphql"

export const AP_LEDGER_PAGE_SIZE = 50

// Field-ordered rather than JSON.stringify(filter), so key order cannot affect the result.
const toFilterKey = (filter: VendorApLedgerListFilter): string =>
  JSON.stringify([
    filter.query ?? null,
    filter.invoiceActions ?? null,
    filter.datePostedFrom ?? null,
    filter.datePostedTo ?? null,
    filter.invoiceDateFrom ?? null,
    filter.invoiceDateTo ?? null,
  ])

interface UseGetVendorApLedgerEntriesParams {
  companyId: string
  filter: VendorApLedgerListFilter
}

interface UseGetVendorApLedgerEntriesReturn {
  entries: VendorApLedgerEntry[]
  isFetching: boolean
  error: string | undefined
  hasMore: boolean
  loadMore: () => void
}

export const useGetVendorApLedgerEntries = ({
  companyId,
  filter,
}: UseGetVendorApLedgerEntriesParams): UseGetVendorApLedgerEntriesReturn => {
  const [limit, setLimit] = useState(AP_LEDGER_PAGE_SIZE)

  // Compared by value, not reference: an inline filter object would otherwise be a new reference
  // every render and reset the window forever.
  const filterKey = toFilterKey(filter)
  const [prevFilterKey, setPrevFilterKey] = useState(filterKey)
  if (prevFilterKey !== filterKey) {
    setPrevFilterKey(filterKey)
    setLimit(AP_LEDGER_PAGE_SIZE)
  }

  const [{ data, fetching, error }] = useQuery({
    query: GetVendorApLedgerEntriesDocument,
    variables: {
      input: {
        vendorCompanyId: companyId,
        filter,
        sort: { field: VendorApLedgerListSortField.TransactionDate, order: SortingOrder.Desc },
        // A growing window from offset 0, not an accumulating offset. `apBalance` is a window
        // function, so Postgres evaluates it over the whole filtered set before applying LIMIT —
        // re-reading from 0 keeps every row's running balance correct with no client-side merge.
        offset: 0,
        limit,
      },
    },
  })

  const output = data?.vendorApLedger.list
  const failureMessage = output?.__typename === "VendorApLedgerListFailure" ? output.error.message : undefined

  const entries = useMemo<VendorApLedgerEntry[]>(
    () => (output?.__typename === "VendorApLedgerListSuccess" ? (output.entries as VendorApLedgerEntry[]) : []),
    [output]
  )

  // VendorApLedgerListSuccess carries no total, so a full page is the only available signal that
  // more may exist.
  const hasMore = entries.length === limit

  const loadMore = useCallback(() => {
    if (fetching) return
    setLimit((prev) => prev + AP_LEDGER_PAGE_SIZE)
  }, [fetching])

  return {
    entries,
    isFetching: fetching,
    error: error?.message ?? failureMessage,
    hasMore,
    loadMore,
  }
}
